/**
 * GeoJSON Conversion Utilities
 *
 * Provides conversion between internal {lat, lng} coordinate format and
 * RFC 7946 GeoJSON Polygon format, plus file parsing for GeoJSON and KML.
 *
 * CRITICAL: RFC 7946 GeoJSON uses [longitude, latitude] coordinate order.
 * Internal app format uses {lat, lng} objects.
 *
 * Reference: /agent-os/specs/2026-02-27-field-boundary-editor/spec.md
 */

import { GeoJsonPolygon } from '../data-models/geo-json-polygon.model';

interface Coordinate {
  lat: number;
  lng: number;
}


/**
 * Convert an array of {lat, lng} coordinates to an RFC 7946 GeoJSON Polygon.
 * Coordinate order in output: [longitude, latitude] per RFC 7946 spec.
 * Automatically closes the ring (first point = last point) if not already closed.
 */
export function coordsToGeoJsonPolygon(coordinates: Coordinate[]): GeoJsonPolygon {
  if (coordinates.length < 3) {
    throw new Error('A polygon requires at least 3 coordinates');
  }

  // Convert to [lng, lat] pairs (RFC 7946 order)
  const ring: number[][] = coordinates.map(c => [c.lng, c.lat]);

  // Close the ring if not already closed
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push([first[0], first[1]]);
  }

  return {
    type: 'Polygon',
    coordinates: [ring]
  };
}

/**
 * Convert a GeoJSON Polygon to an array of {lat, lng} coordinates.
 * Reads the exterior ring (first ring) and converts from [lng, lat] to {lat, lng}.
 * Removes the closing duplicate point if present.
 */
export function geoJsonPolygonToCoords(polygon: GeoJsonPolygon): Coordinate[] {
  if (!polygon || polygon.type !== 'Polygon' || !Array.isArray(polygon.coordinates)) {
    return [];
  }

  const exteriorRing = polygon.coordinates[0];
  if (!Array.isArray(exteriorRing) || exteriorRing.length === 0) {
    return [];
  }

  // Convert from [lng, lat] to {lat, lng}
  const coords: Coordinate[] = exteriorRing.map(point => ({
    lat: point[1],
    lng: point[0]
  }));

  // Remove closing duplicate point if present
  if (coords.length > 1) {
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (first.lat === last.lat && first.lng === last.lng) {
      coords.pop();
    }
  }

  return coords;
}

/**
 * Parse a GeoJSON file string and extract coordinates from the first Polygon found.
 * Supports GeoJSON types: Polygon, MultiPolygon, Feature, and FeatureCollection.
 * For MultiPolygon, takes the first polygon.
 * For FeatureCollection, takes the first Feature containing a Polygon or MultiPolygon.
 */
export function parseGeoJsonFile(content: string): Coordinate[] {
  let geojson: any;

  try {
    geojson = JSON.parse(content);
  } catch {
    throw new Error('Invalid JSON content');
  }

  return extractCoordsFromGeoJson(geojson);
}

/**
 * Extract polygon coordinates from a KML XML Document's <Polygon> elements.
 * Returns null if no <Polygon> element is found (caller should fall back to
 * convex hull from <LineString> coordinates).
 *
 * KML coordinate format: longitude,latitude,altitude (space-separated)
 */
export function extractPolygonFromKml(xmlDoc: Document): Coordinate[] | null {
  // Look for Polygon elements
  const polygonElements = xmlDoc.getElementsByTagName('Polygon');

  if (polygonElements.length === 0) {
    return null;
  }

  // Extract coordinates from the first Polygon's outerBoundaryIs
  const polygon = polygonElements[0];
  const outerBoundary = polygon.getElementsByTagName('outerBoundaryIs')[0];

  let coordElement: Element | undefined;
  if (outerBoundary) {
    coordElement = outerBoundary.getElementsByTagName('coordinates')[0];
  }

  // Fallback: look for coordinates directly within the Polygon
  if (!coordElement) {
    coordElement = polygon.getElementsByTagName('coordinates')[0];
  }

  if (!coordElement) {
    return null;
  }

  const coordText = coordElement.textContent?.trim();
  if (!coordText) {
    return null;
  }

  const coordinates: Coordinate[] = [];

  // KML coordinates are: longitude,latitude,altitude (space or newline separated)
  const coordPairs = coordText.split(/\s+/).filter(s => s.length > 0);

  for (const pair of coordPairs) {
    const parts = pair.split(',');
    if (parts.length >= 2) {
      const lng = parseFloat(parts[0]);
      const lat = parseFloat(parts[1]);

      if (!isNaN(lat) && !isNaN(lng)) {
        coordinates.push({ lat, lng });
      }
    }
  }

  if (coordinates.length === 0) {
    return null;
  }

  // Remove closing duplicate point if present
  if (coordinates.length > 1) {
    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];
    if (first.lat === last.lat && first.lng === last.lng) {
      coordinates.pop();
    }
  }

  return coordinates;
}

// -- Internal helpers --

/**
 * Recursively extract coordinates from any GeoJSON type.
 */
function extractCoordsFromGeoJson(geojson: any): Coordinate[] {
  if (!geojson || typeof geojson !== 'object') {
    throw new Error('Invalid GeoJSON object');
  }

  switch (geojson.type) {
    case 'Polygon':
      return polygonCoordsToLatLng(geojson.coordinates);

    case 'MultiPolygon':
      if (Array.isArray(geojson.coordinates) && geojson.coordinates.length > 0) {
        // Take the first polygon from the MultiPolygon
        return polygonCoordsToLatLng(geojson.coordinates[0]);
      }
      throw new Error('MultiPolygon has no coordinate data');

    case 'Feature':
      if (geojson.geometry) {
        return extractCoordsFromGeoJson(geojson.geometry);
      }
      throw new Error('Feature has no geometry');

    case 'FeatureCollection':
      if (Array.isArray(geojson.features)) {
        // Find the first Feature with a Polygon or MultiPolygon geometry
        for (const feature of geojson.features) {
          if (feature?.geometry?.type === 'Polygon' || feature?.geometry?.type === 'MultiPolygon') {
            return extractCoordsFromGeoJson(feature.geometry);
          }
        }
      }
      throw new Error('FeatureCollection contains no Polygon or MultiPolygon features');

    default:
      throw new Error(`Unsupported GeoJSON type: ${geojson.type}`);
  }
}

/**
 * Convert GeoJSON polygon coordinate rings ([lng, lat] format) to {lat, lng} array.
 * Uses only the exterior ring (first ring).
 */
function polygonCoordsToLatLng(rings: number[][][]): Coordinate[] {
  if (!Array.isArray(rings) || rings.length === 0) {
    throw new Error('Polygon has no coordinate rings');
  }

  const exteriorRing = rings[0];
  if (!Array.isArray(exteriorRing) || exteriorRing.length === 0) {
    throw new Error('Polygon exterior ring is empty');
  }

  const coords: Coordinate[] = exteriorRing.map(point => ({
    lat: point[1],
    lng: point[0]
  }));

  // Remove closing duplicate point if present
  if (coords.length > 1) {
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (first.lat === last.lat && first.lng === last.lng) {
      coords.pop();
    }
  }

  return coords;
}
